import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QButton, QStack, useCurrentUser } from '@qualio/ui-components';
import { useNavigate } from 'react-router-dom';
import { EventStep, User, UpdateForm } from '../../../types';
import StepContainer from './StepContainer';
import StepHeader from './StepHeader';
import StepBody from './StepBody';
import EmptyStep from './EmptyStep';
import { useEventPermissions } from '../../../hooks';
import FormStepMetadata from './FormStepMetadata/FormStepMetadata';
import { useAssociateForm } from '../../../hooks/useAssociateForm';
import { useFormDetails } from '../../../hooks/useEventForm';
import ViewFormButton from './ViewFormButton';
import FormApproversList from './FormApproversList';
import { useQuery } from 'react-query';
import approversApi from '../../../api/approvers.api';
import AssignFormModal from '../../../views/EventStepForm/components/AssignFormModal';
import DigitalSignatureModal from '../../DigitalSignatureModal/DigitalSignatureModal';
import { DigitalSignature, DigitalSignatureInput } from '../../../types/digitalSignature';
import StepStartTimeConstraintInfo from './StepStartTimeConstraintInfo';
import * as DisplayStrings from '../../../displayStrings';
import { isStepNotCompleted } from '../../../utils/eventUtils';
import { useRevertIssueStep } from '../../../hooks/useRevertIssueStep';

type FormStepProps = {
  isResolutionStepForApproval: boolean;
  step: EventStep;
  users: User[];
  lastUpdated: number | null;
  refetch: () => void;
  isResolutionStep?: boolean;
};

const FormStep = ({
  isResolutionStepForApproval,
  step,
  users,
  lastUpdated,
  refetch,
  isResolutionStep = false,
}: FormStepProps) => {
  const { t } = useTranslation();
  const stepId = step.id;
  const eventId = step.issue_id;
  const formId = step.form_id;
  const { canAddFormToStep, canRevertStep } = useEventPermissions();
  const stepStatus = step.status;
  const stepNotCompleted = isStepNotCompleted(stepStatus);
  const { companyId } = useCurrentUser();
  const [isAssignFormModalOpen, setIsAssignFormModalOpen] = useState<boolean>(false);
  const [isRevertStepModalOpen, setIsRevertStepModalOpen] = useState(false);

  const navigate = useNavigate();

  const { data: form, isLoading: isFormLoading } = useFormDetails(eventId, stepId, formId as number);
  const { data: existingReviewers, isLoading: isReviewersLoading } = useQuery(
    ['reviewersList', formId],
    () => approversApi.getApprovers(companyId, formId as number, 'reviewers'),
    { enabled: !!formId, staleTime: 5 * 60 * 1000 },
  );

  const { data: existingApprovers, isLoading: isApproversLoading } = useQuery(
    ['approversList', formId],
    () => approversApi.getApprovers(companyId, formId as number, 'approvers'),
    { enabled: !!formId, staleTime: 5 * 60 * 1000 },
  );
  const { mutate: associateFormMutate, isLoading: isAssociateFormLoading } = useAssociateForm();
  const { mutateAsync: revertIssueStepMutate } = useRevertIssueStep(step.issue_id, step.id);
  const navigateToFormView = useCallback(
    (form: UpdateForm) => {
      navigate(`/events/${eventId}/steps/${stepId}/forms/${form.id}`);
    },
    [eventId, navigate, stepId],
  );
  const navigateToFormEdit = useCallback(
    (form: UpdateForm) => {
      navigate(`/events/${eventId}/steps/${stepId}/forms/${form.id}/edit`);
    },
    [eventId, navigate, stepId],
  );

  const assignStepButton = () => {
    return (
      <QButton
        variant="ghost"
        leftIcon="User"
        isDisabled={!canAddFormToStep}
        onClick={() => setIsAssignFormModalOpen(true)}
        data-metrics="assign-step-button"
        data-cy="assign-step"
      >
        {t('tasks.actions.assignStep')}
      </QButton>
    );
  };

  const revertStepModalInput: DigitalSignatureInput = {
    headingText: t('tasks.actions.revertStepCompletion'),
    subText: t('tasks.modals.signOffToRevertCompletion', { stepLabel: step.label }),
    submitButtonText: t('tasks.actions.revertStep'),
    successToastHeader: t('common.labels.stepReverted'),
    successToastDescription: t('tasks.modals.stepCompletionWasReverted', { stepLabel: step.label }),
  };

  const handleRevertStep = useCallback(
    async (payload: DigitalSignature) => {
      await revertIssueStepMutate(payload);
      refetch();
    },
    [refetch, revertIssueStepMutate],
  );

  const RevertStepButton = () => (
    <QButton
      variant="ghost"
      leftIcon="CornerUpLeft"
      onClick={() => setIsRevertStepModalOpen(true)}
      isDisabled={!canRevertStep || isResolutionStepForApproval}
      data-metrics="step-header-revert-step-button"
    >
      {t('common.labels.revert')}
    </QButton>
  );

  return (
    <StepContainer>
      <StepHeader
        title={step.label}
        status={stepStatus}
        headerButton={
          formId ? <ViewFormButton eventId={eventId} stepId={stepId} formId={formId} /> : assignStepButton()
        }
        revertStepButton={<RevertStepButton />}
      />
      <StepBody isLoading={isFormLoading || isReviewersLoading || isApproversLoading}>
        <FormStepMetadata form={form} />
        {!!step.start_time_constraint && form?.status === 'draft' && isResolutionStep && (
          <StepStartTimeConstraintInfo startTimeConstraint={step.start_time_constraint} lastUpdated={lastUpdated} />
        )}
        {formId && form ? (
          <>
            <QStack direction="row" spacing={20} m={4}>
              <FormApproversList label={t('common.labels.approvers')} approvers={existingApprovers ?? []} />
              <FormApproversList label={t('common.labels.reviewers')} approvers={existingReviewers ?? []} />
            </QStack>
          </>
        ) : (
          <EmptyStep
            text={
              stepNotCompleted
                ? t('tasks.messages.completeFormAndSendForReview')
                : t('tasks.messages.noFormAssociatedWithStep')
            }
          >
            {stepNotCompleted && (
              <QButton
                variant="ghost"
                leftIcon="Edit3"
                onClick={() => associateFormMutate({ eventId, stepId }, { onSuccess: navigateToFormEdit })}
                isLoading={isAssociateFormLoading}
                isDisabled={!canAddFormToStep}
                data-cy="complete-form"
              >
                {DisplayStrings.CompleteForm}
              </QButton>
            )}
          </EmptyStep>
        )}
      </StepBody>
      <AssignFormModal
        isOpen={isAssignFormModalOpen}
        setIsOpen={setIsAssignFormModalOpen}
        onSave={(rawOwnerId) => {
          const ownerId = Number(rawOwnerId);
          associateFormMutate({ eventId, stepId, ownerId }, { onSuccess: navigateToFormView });
        }}
        users={users ?? []}
        reassign={false}
        eventStepName={step.label}
      />
      <DigitalSignatureModal
        isOpen={isRevertStepModalOpen}
        setIsOpen={setIsRevertStepModalOpen}
        onSave={handleRevertStep}
        inputTexts={revertStepModalInput}
        isCommentRequired={false}
      />
    </StepContainer>
  );
};

export default FormStep;
